import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BioEmphasisTheme, EngagementBand, RelationshipTier } from '@toiyp/shared';
import type { AvatarView } from '../profiles/api';
import { API_ORIGIN } from '../../config';
import { withUnlockHeader } from '../hidden/hiddenSlice';

export interface RelationshipWithPeer {
  id: string;
  status: 'pending' | 'accepted' | 'paused' | 'ended';
  /** Derived relationship tier (RT-1) — the capability key. */
  tier: RelationshipTier;
  /** Fixed-copy reason keys explaining the tier. */
  tierReasons: string[];
  /** What this pair may do right now: [] below `trusted`. */
  capabilities: string[];
  /** Derived engagement (engagement-v1) — the list sorts on this, most engaged first. */
  engagement: number | null;
  /** True when I keep this pair behind my secret code (only ever true while unlocked). */
  hidden: boolean;
  /**
   * They have a saved draft reply to my arrived letter (the pen). Never the
   * words, never a live typing signal, and never for a pair they keep hidden.
   */
  peerWriting?: boolean;
  peer: {
    userId: string;
    handle: string | null;
    displayName: string | null;
    bio: string | null;
    avatar: AvatarView | null;
    /** Their birthday as `MM-DD`, for an accepted pair only — no year. */
    birthday: string | null;
  } | null;
}

/** The F4 bio card payload — depth-projected by the server (ENGAGEMENT_PLAN §5.2). */
export interface BioView {
  relationshipId: string;
  depth: 'basic' | 'hot' | 'trusted';
  tier: RelationshipTier;
  tierReasons: string[];
  engagementBand: EngagementBand | null;
  bandFallback: boolean;
  letterCount: number;
  completedExchanges: number;
  reciprocalRatio: number | null;
  daysSinceLastLetter: number | null;
  lastLetterAt: string | null;
  rekindle: boolean;
  /** Verdict-powered honesty line; quarantined-by-calibration fields come back null. */
  oneSided: boolean | null;
  emphasis: BioEmphasisTheme | null;
  lockedCapabilities: Array<'contact' | 'photos' | 'history'>;
  /** Plain-code lexicon themes — the subjects of your letters (2026-02-10). */
  themes: { theme: string; count: number }[];
  /** hot+ */
  responseWindowHours?: number | null;
  perDirection?: { mine: number; theirs: number };
  toneCounts?: { warm: number; neutral: number; tense: number };
}

/** The F5 history timeline — `trusted` only, server-enforced. */
export interface HistoryView {
  relationshipId: string;
  firstLetterAt: string | null;
  letterCount: number;
  completedExchanges: number;
  milestones: { letterNumber: number; sealedAt: string | null }[];
  longestSilenceDays: number | null;
  toneSeries: { sealedAt: string; tone: 'warm' | 'neutral' | 'tense' }[];
  /** Deterministic drift chapter (Jev tone.drift was dropped by calibration). */
  drift: 'warmer' | 'cooler' | null;
  tierHistory: { tier: RelationshipTier; changedAt: string; reasons: string[] }[];
  engagementBand: EngagementBand | null;
  bandFallback: boolean;
}

/** One member's contact offer to one relationship. */
export interface ContactView {
  ownerId: string;
  phone: string | null;
  externalEmail: string | null;
  address: string | null;
}

/** My private note about a contact. Empty body = no note. */
export interface ContactNote {
  body: string;
  updatedAt: string | null;
}

/** Where the two of you met — shared, either side may edit. */
export interface MetView {
  place: string | null;
  event: string | null;
  /** `YYYY-MM-DD`. */
  metOn: string | null;
  byMe: boolean;
  updatedByHandle: string | null;
  updatedAt: string;
}

export interface HiddenStatus {
  hasCode: boolean;
  hiddenCount: number | null;
}

/** One row of the profile's "Hidden & blocked" list (set-aside.ts on the API). */
export interface SetAsideView {
  relationshipId: string;
  /** `removed` is a refused overture taken off the list. */
  kind: 'hidden' | 'blocked' | 'removed';
  since: string;
  peer: { handle: string | null; displayName: string | null; avatar: AvatarView | null } | null;
  /** For a removed overture: the address it came from. */
  fromEmail: string | null;
}

export interface UnlockResult {
  token: string;
  expiresAt: string;
}

export const relationshipsApi = createApi({
  reducerPath: 'relationshipsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_ORIGIN}/api/relationships`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => withUnlockHeader(headers, getState),
  }),
  tagTypes: ['Relationship', 'Contact', 'Bio', 'History', 'Hidden', 'Note', 'Met', 'SetAside'],
  // Coming back to the app (tab focus, reopened PWA, network back) shows fresh mail.
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: (b) => ({
    mine: b.query<RelationshipWithPeer[], void>({
      query: () => '',
      providesTags: ['Relationship'],
    }),
    /** Begin a correspondence with someone by their @handle. Idempotent. */
    start: b.mutation<RelationshipWithPeer, { handle: string }>({
      query: (body) => ({ url: '', method: 'POST', body }),
      invalidatesTags: ['Relationship'],
    }),
    /** My own contact offer to this pair — visible regardless of trust. */
    myContact: b.query<ContactView | null, { relationshipId: string }>({
      query: ({ relationshipId }) => `/${relationshipId}/contact/me`,
      providesTags: ['Contact'],
    }),
    /** The peer's offer — server 403s below `trusted`. */
    peerContact: b.query<ContactView | null, { relationshipId: string }>({
      query: ({ relationshipId }) => `/${relationshipId}/contact`,
      providesTags: ['Contact'],
      // 403 is "not trusted yet", not an error state to surface — treat as "no
      // contact". Other failures (network, 404) still reject.
      transformErrorResponse: (res) => (res.status === 403 ? undefined : res),
    }),
    shareContact: b.mutation<
      ContactView,
      { relationshipId: string; phone?: string; externalEmail?: string; address?: string }
    >({
      query: ({ relationshipId, ...body }) => ({
        url: `/${relationshipId}/contact`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Contact', 'Relationship'],
    }),
    removeContact: b.mutation<void, { relationshipId: string }>({
      query: ({ relationshipId }) => ({
        url: `/${relationshipId}/contact`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Contact', 'Relationship'],
    }),
    /** The pair's bio card (F4) — depth-projected by tier server-side. */
    bio: b.query<BioView, { relationshipId: string }>({
      query: ({ relationshipId }) => `/${relationshipId}/bio`,
      providesTags: ['Bio'],
    }),
    /** The pair's history timeline (F5). 403 below `trusted` reads as "not yet". */
    history: b.query<HistoryView, { relationshipId: string }>({
      query: ({ relationshipId }) => `/${relationshipId}/history`,
      providesTags: ['History'],
      transformErrorResponse: (res) => (res.status === 403 ? undefined : res),
    }),
    /** My private note about this contact — nobody else can read it. */
    note: b.query<ContactNote, { relationshipId: string }>({
      query: ({ relationshipId }) => `/${relationshipId}/note`,
      providesTags: (_r, _e, { relationshipId }) => [{ type: 'Note', id: relationshipId }, 'Note'],
    }),
    saveNote: b.mutation<ContactNote, { relationshipId: string; body: string }>({
      query: ({ relationshipId, body }) => ({ url: `/${relationshipId}/note`, method: 'PUT', body: { body } }),
      invalidatesTags: (_r, _e, { relationshipId }) => [{ type: 'Note', id: relationshipId }],
    }),
    /** Where we met — null until one of you records it. */
    met: b.query<MetView | null, { relationshipId: string }>({
      query: ({ relationshipId }) => `/${relationshipId}/met`,
      providesTags: (_r, _e, { relationshipId }) => [{ type: 'Met', id: relationshipId }, 'Met'],
    }),
    saveMet: b.mutation<
      MetView | null,
      { relationshipId: string; place: string | null; event: string | null; metOn: string | null }
    >({
      query: ({ relationshipId, ...body }) => ({ url: `/${relationshipId}/met`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { relationshipId }) => [{ type: 'Met', id: relationshipId }],
    }),
    /** Is a secret code set? `hiddenCount` comes back only while unlocked. */
    hiddenStatus: b.query<HiddenStatus, void>({
      query: () => '/hidden/status',
      providesTags: ['Hidden'],
    }),
    /** Set, change (with `currentCode`) or, after a fresh sign-in, reset my code. */
    setHiddenCode: b.mutation<UnlockResult, { code: string; currentCode?: string }>({
      query: (body) => ({ url: '/hidden/code', method: 'PUT', body }),
      invalidatesTags: ['Hidden'],
    }),
    /** Whatever was typed into search. A miss is `{ token: null }`, not an error. */
    unlockHidden: b.mutation<UnlockResult | { token: null; expiresAt: null }, { code: string }>({
      query: (body) => ({ url: '/hidden/unlock', method: 'POST', body }),
    }),
    hide: b.mutation<void, { relationshipId: string }>({
      query: ({ relationshipId }) => ({ url: `/${relationshipId}/hidden`, method: 'PUT' }),
      invalidatesTags: ['Relationship', 'Hidden'],
    }),
    unhide: b.mutation<void, { relationshipId: string }>({
      query: ({ relationshipId }) => ({ url: `/${relationshipId}/hidden`, method: 'DELETE' }),
      invalidatesTags: ['Relationship', 'Hidden'],
    }),
    /** What I have hidden from my list, blocked, or removed. Only ever my side. */
    setAsideList: b.query<SetAsideView[], void>({
      query: () => '/set-aside',
      providesTags: ['SetAside'],
    }),
    /** Hide a pair from my list, or block the other person. */
    setAside: b.mutation<void, { relationshipId: string; kind: 'hidden' | 'blocked' }>({
      query: ({ relationshipId, kind }) => ({ url: `/${relationshipId}/set-aside`, method: 'PUT', body: { kind } }),
      invalidatesTags: ['Relationship', 'SetAside'],
    }),
    /** Unhide (also restores a removed overture) or unblock. */
    restoreSetAside: b.mutation<void, { relationshipId: string; kind: 'hidden' | 'blocked' }>({
      query: ({ relationshipId, kind }) => ({ url: `/${relationshipId}/set-aside/${kind}`, method: 'DELETE' }),
      invalidatesTags: ['Relationship', 'SetAside'],
    }),
    /** Take a refused overture off my list. */
    removeOverture: b.mutation<void, { relationshipId: string }>({
      query: ({ relationshipId }) => ({ url: `/${relationshipId}/set-aside/overture`, method: 'PUT' }),
      invalidatesTags: ['Relationship', 'SetAside'],
    }),
  }),
});

export const {
  useMineQuery,
  useStartMutation,
  useMyContactQuery,
  usePeerContactQuery,
  useShareContactMutation,
  useRemoveContactMutation,
  useBioQuery,
  useHistoryQuery,
  useNoteQuery,
  useSaveNoteMutation,
  useMetQuery,
  useSaveMetMutation,
  useHiddenStatusQuery,
  useSetHiddenCodeMutation,
  useUnlockHiddenMutation,
  useHideMutation,
  useUnhideMutation,
  useSetAsideListQuery,
  useSetAsideMutation,
  useRestoreSetAsideMutation,
  useRemoveOvertureMutation,
} = relationshipsApi;